"use client";

import { useState } from "react";
import { Img } from "@/components/image";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { DietHubSectionHeader } from "@/features/hubs/components/diet-hub-section-header";
import type {
  DietHubContentOrderedFeaturesSectionType,
  DietHubOrderedFeatureType,
} from "@/features/hubs/types/diet-hub.type";
import { useCarouselDrag } from "@/lib/hooks/use-carousel-drag";
import { strapiUrl } from "@/lib/strapi";
import { cn } from "@/lib/utils";

const DietHubOrderedFeatureCard = ({
  feature,
  inArticle,
}: {
  feature: DietHubOrderedFeatureType;
  inArticle: boolean;
}) => {
  return (
    <div
      className={cn(
        "flex h-full flex-col gap-y-5 rounded-lg border border-border",
        inArticle ? "p-6" : "p-10",
      )}
    >
      {feature.icon?.url ? (
        <Img
          src={strapiUrl(feature.icon.url)}
          alt={feature.icon.alternativeText ?? ""}
          width={feature.icon.width}
          height={feature.icon.height}
          className="size-16 shrink-0 object-cover"
        />
      ) : null}
      <div className="flex items-center gap-x-3">
        <span className="font-medium text-base text-brand-violet tracking-wide">
          {feature.index.toString().padStart(2, "0")}
        </span>
        <h3 className="font-medium text-brand-green1-300 text-lg">
          {feature.title}
        </h3>
      </div>
      <p className="text-base text-brand-grey5">{feature.description}</p>
    </div>
  );
};

export const DietHubOrderedFeatures = ({
  data,
  inArticle = false,
}: {
  data: DietHubContentOrderedFeaturesSectionType;
  inArticle?: boolean;
}) => {
  const sortedFeatures = [...data.features].sort((a, b) => a.index - b.index);

  const [api, setApi] = useState<CarouselApi>();

  const isDragging = useCarouselDrag(api);

  return (
    <section className={inArticle ? "my-12" : "container my-18 md:my-30"}>
      <div className={cn("flex flex-col gap-8", !inArticle && "md:gap-16")}>
        <DietHubSectionHeader
          label={null}
          title={data.title}
          callback={data.callback ?? null}
          description={data.description}
        />
        <div
          className={
            'relative -mr-4 after:absolute after:top-0 after:right-0 after:h-full after:w-14 after:bg-linear-to-r after:from-transparent after:to-white after:content-[""] md:mr-0 md:after:content-none'
          }
        >
          <Carousel
            opts={{
              align: "start",
              breakpoints: { "(min-width: 768px)": { active: false } },
            }}
            setApi={setApi}
          >
            <CarouselContent
              className={cn(
                "last:mr-10 md:ml-0 md:grid md:grid-cols-2 md:gap-6 md:last:mr-0",
                !inArticle && "lg:grid-cols-3",
              )}
            >
              {sortedFeatures.map((feature) => (
                <CarouselItem
                  className="basis-[88%] md:basis-auto md:pl-0"
                  key={feature.index}
                >
                  <DietHubOrderedFeatureCard
                    feature={feature}
                    inArticle={inArticle}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            {!isDragging && (
              <>
                <CarouselPrevious className="left-0 size-12 rounded-md border-border drop-shadow-[2px_12px_7px_hsla(0,0%,0%,0.06)] hover:bg-background/90 disabled:invisible sm:-left-4 md:hidden" />
                <CarouselNext className="right-4 z-10 size-12 rounded-md border-border drop-shadow-[2px_12px_7px_hsla(0,0%,0%,0.06)] hover:bg-background/90 disabled:invisible sm:right-10 md:hidden" />
              </>
            )}
          </Carousel>
        </div>
      </div>
    </section>
  );
};
